import React, { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Briefcase,
  Building2,
  ChevronRight,
  Layers,
  MapPin,
  MoreVertical,
} from 'lucide-react';
import { PartnerEntity } from '../../../../../utils/types/admin_types';
import {
  DEFAULT_TREE_LEVEL_STYLE,
  TREE_LEVEL_STYLES,
} from '../constants';
import { EntityAccordionNodeProps } from '../interfaces';
import { EntityContextMenu } from './EntityContextMenu';

export const EntityAccordionNode: React.FC<EntityAccordionNodeProps> = ({
  entity,
  level = 0,
  expandedEntities,
  onToggle,
  onEditClick,
  onDeleteClick,
  onAddClick,
  searchQuery,
}) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [contextMenuPos, setContextMenuPos] = useState<{
    top: number;
    left: number;
  } | null>(null);

  const hasChildren = Boolean(entity.subEntities && entity.subEntities.length > 0);
  const isExpanded = expandedEntities.includes(entity.id);
  const levelStyle = TREE_LEVEL_STYLES[level] || DEFAULT_TREE_LEVEL_STYLE;

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    event.preventDefault();
    setMenuAnchorEl(event.currentTarget);
    setContextMenuPos(null);
  };

  const handleContextMenu = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setContextMenuPos({
      top: event.clientY,
      left: event.clientX,
    });
    setMenuAnchorEl(null);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
    setContextMenuPos(null);
  };

  const renderNodeIcon = () => {
    if (level === 0) {
      return (
        <Box
          sx={{
            width: 22,
            height: 22,
            borderRadius: '5px',
            bgcolor: levelStyle.iconBg,
            color: levelStyle.iconColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Building2 size={13} />
        </Box>
      );
    }
    if (level === 1) {
      return (
        <Box
          sx={{
            width: 20,
            height: 20,
            borderRadius: '4px',
            bgcolor: levelStyle.iconBg,
            color: levelStyle.iconColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Layers size={12} />
        </Box>
      );
    }
    return (
      <Box
        sx={{
          width: 18,
          height: 18,
          borderRadius: '4px',
          bgcolor: levelStyle.iconBg,
          color: levelStyle.iconColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Briefcase size={11} />
      </Box>
    );
  };

  const locationText = entity.location?.city || entity.location?.country;
  const isMenuOpen = Boolean(menuAnchorEl) || Boolean(contextMenuPos);

  return (
    <>
      <Accordion
        expanded={hasChildren ? isExpanded : false}
        onChange={hasChildren ? () => onToggle(entity.id) : undefined}
        onContextMenu={handleContextMenu}
        disableGutters
        elevation={0}
        sx={{
          border: levelStyle.border,
          borderRadius: `${levelStyle.borderRadius} !important`,
          bgcolor: levelStyle.bgcolor,
          overflow: 'hidden',
          mb: `${levelStyle.mb}px`,
          '&:before': { display: 'none' },
          transition: 'all 0.15s ease-in-out',
          boxShadow: isExpanded
            ? '0 2px 8px rgba(0, 0, 0, 0.04)'
            : '0 1px 2px rgba(0, 0, 0, 0.02)',
          '&:hover': {
            borderColor: '#cbd5e1',
            boxShadow: '0 2px 6px rgba(0, 0, 0, 0.04)',
          },
        }}
      >
        <AccordionSummary
          expandIcon={null}
          sx={{
            minHeight: `${levelStyle.minHeight}px !important`,
            height: `${levelStyle.minHeight}px`,
            px: 1.25,
            py: 0,
            cursor: hasChildren ? 'pointer' : 'default',
            '& .MuiAccordionSummary-content': {
              my: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              overflow: 'hidden',
            },
            '&:hover': {
              bgcolor: 'rgba(0, 0, 0, 0.012)',
            },
          }}
        >
          {/* Left Side: Chevron on the Left, Hierarchy Icon, Name, Chips */}
          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}
          >
            {/* Expand Chevron or leaf dot */}
            {hasChildren ? (
              <Box
                component="span"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggle(entity.id);
                }}
                sx={{
                  width: 18,
                  height: 18,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: '#64748b',
                  borderRadius: '4px',
                  flexShrink: 0,
                  transition: 'transform 0.15s ease-in-out, color 0.15s',
                  transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                  '&:hover': {
                    color: '#0f172a',
                    bgcolor: 'rgba(0,0,0,0.05)',
                  },
                }}
              >
                <ChevronRight size={13} />
              </Box>
            ) : (
              <Box
                sx={{
                  width: 18,
                  height: 18,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Box
                  sx={{
                    width: 3.5,
                    height: 3.5,
                    borderRadius: '50%',
                    bgcolor: '#cbd5e1',
                  }}
                />
              </Box>
            )}

            {renderNodeIcon()}

            <Typography
              noWrap
              sx={{
                fontWeight: levelStyle.fontWeight,
                fontSize: levelStyle.fontSize,
                color: '#0f172a',
                letterSpacing: '-0.005em',
                flexShrink: 1,
              }}
            >
              {entity.name}
            </Typography>

            {hasChildren && (
              <Chip
                size="small"
                label={`${entity.subEntities!.length} ${
                  entity.subEntities!.length === 1 ? 'sub-entity' : 'sub-entities'
                }`}
                sx={{
                  height: 18,
                  fontSize: '0.65rem',
                  fontWeight: 600,
                  bgcolor: '#f1f5f9',
                  color: '#475569',
                  borderRadius: '4px',
                  border: '1px solid #e2e8f0',
                  flexShrink: 0,
                  '& .MuiChip-label': { px: 0.6 },
                }}
              />
            )}

            {entity.alias && (
              <Chip
                size="small"
                variant="outlined"
                label={entity.alias}
                sx={{
                  height: 17,
                  fontSize: '0.625rem',
                  borderColor: '#e2e8f0',
                  color: '#64748b',
                  borderRadius: '3px',
                  flexShrink: 0,
                  display: { xs: 'none', md: 'inline-flex' },
                  '& .MuiChip-label': { px: 0.5 },
                }}
              />
            )}

            {locationText && (
              <Stack
                direction="row"
                spacing={0.3}
                alignItems="center"
                sx={{
                  color: '#94a3b8',
                  fontSize: '0.7rem',
                  flexShrink: 0,
                  display: { xs: 'none', sm: 'inline-flex' },
                }}
              >
                <MapPin size={11} />
                <span>{locationText}</span>
              </Stack>
            )}
          </Stack>

          {/* Right Side: Clean Context Menu Action */}
          <Stack
            direction="row"
            spacing={0.25}
            alignItems="center"
            onClick={(e) => e.stopPropagation()}
            sx={{ flexShrink: 0 }}
          >
            <Tooltip title="Actions (or right-click)" arrow placement="top">
              <IconButton
                size="small"
                aria-label="Entity options"
                onClick={handleOpenMenu}
                sx={{
                  p: 0.35,
                  borderRadius: '5px',
                  color: '#64748b',
                  transition: 'all 0.15s ease-in-out',
                  '&:hover': {
                    color: '#0f172a',
                    bgcolor: 'rgba(0, 0, 0, 0.05)',
                  },
                }}
              >
                <MoreVertical size={14} />
              </IconButton>
            </Tooltip>
          </Stack>
        </AccordionSummary>

        {hasChildren && (
          <AccordionDetails
            sx={{
              pt: 0.35,
              pb: 0.75,
              pl: { xs: 1.5, sm: 2 },
              pr: { xs: 0.5, sm: 1 },
              borderLeft: '1.5px dashed #cbd5e1',
              ml: { xs: 1.25, sm: 1.75 },
              mb: 0.25,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.35,
            }}
          >
            {entity.subEntities!.map((child: PartnerEntity) => (
              <EntityAccordionNode
                key={child.id}
                entity={child}
                level={level + 1}
                expandedEntities={expandedEntities}
                onToggle={onToggle}
                onEditClick={onEditClick}
                onDeleteClick={onDeleteClick}
                onAddClick={onAddClick}
                searchQuery={searchQuery}
              />
            ))}
          </AccordionDetails>
        )}
      </Accordion>

      <EntityContextMenu
        entity={entity}
        anchorEl={menuAnchorEl}
        anchorPosition={contextMenuPos}
        open={isMenuOpen}
        onClose={handleCloseMenu}
        onEditClick={onEditClick}
        onDeleteClick={onDeleteClick}
        onAddClick={onAddClick}
      />
    </>
  );
};
